import React, { ChangeEvent, FC, FocusEvent, ReactElement, useEffect, useState } from "react";
import { NumericString } from "./NumericString";

export type NumericInputRenderer = (value: string, onChange: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void, onFocus: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void, onBlur: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void, disabled?: boolean, decorator?: string, label?: string) => ReactElement;

export interface NumericStringProps {
  id?: string, 
  value?: string, 
  locale?: "us" | "es",
  intOnly?: boolean, 
  positiveOnly?: boolean, 
  displayThousandsDelimiter?: boolean, 
  max?: number,
  min?: number,
  decimals?: number,
  disabled?: boolean,
  roundFiguresOnLengthExceeded?: boolean,
  defaultValue?: string,
  style?: any
  decorator?: string,
  label?: string,
  renderer?: NumericInputRenderer, 
  onChange: (newValue: string) => void
}

export const NumericInput: FC<NumericStringProps> = ({id = "numeric-input-id", value, locale = "es", intOnly = false, positiveOnly = false, displayThousandsDelimiter = true, max = Number.POSITIVE_INFINITY, min = Number.NEGATIVE_INFINITY, decimals = 3, renderer, disabled, defaultValue = "", style, decorator = "", roundFiguresOnLengthExceeded = false, label = "", onChange : onOuterChange}) => {
  
  const builder = NumericString.builder()
    .roundFiguresOnLengthExceeded(roundFiguresOnLengthExceeded)
    .decimals(decimals)
    .locale(locale)
    .value(value ?? defaultValue)
    .intOnly(intOnly)
    .positiveOnly(positiveOnly)
    .displayThousandsDelimiter(displayThousandsDelimiter)
    .max(max)
    .min(min)

  const [innerValue, setInnerValue] = useState<NumericString>(builder.build());

  
  const onFocus = (e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if(innerValue.getPrintingValue() === defaultValue)
        setInnerValue(builder.empty());
  };

  const onBlur = (e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.target.value === defaultValue) setInnerValue(builder.value(defaultValue ?? "").build());
    else {
      let cleanValue  = e.target.value
      if(cleanValue.endsWith(innerValue.decimalDelimiter))  {
        cleanValue = cleanValue.substring(0, e.target.value.length - 1)
      }
      if((cleanValue.startsWith("0") || cleanValue.startsWith("-0")) && (!cleanValue.startsWith("0,") || !cleanValue.startsWith("-0,"))) {
        const candidate = builder.value(cleanValue).build()
        const negativePrefix = (candidate.isNegative() ? "-" : "")
        cleanValue = candidate.getPrintingValue().replace("-", "");
        cleanValue = cleanValue.replace(candidate.thousandsDelimiter, "");
        cleanValue = cleanValue.replace(/\b0+/g, "");
        cleanValue = negativePrefix + (cleanValue.startsWith(",") ? "0" + cleanValue : cleanValue)
      }
      setInnerValue(builder.value(cleanValue).build())
    }
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let newValue = e.target.value;
    let numericString = builder.value(newValue).build()
    handleCaret(e)
    if(numericString.isValid()) {
      setInnerValue(numericString)
    } else if(newValue === "") {
      setInnerValue(builder.empty())
    }
  };

  const handleCaret = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = builder.value(e.target.value).build()
    const before = innerValue.getPrintingValue().split(innerValue.thousandsDelimiter).length
    const after = newValue.getPrintingValue().split(innerValue.thousandsDelimiter).length
    const offset = after - before
    const caret = (e.target.selectionStart ?? 0) + offset - (newValue.isValid() ? 0 : 1)
    const element = e.target;
    window.requestAnimationFrame(() => {
      element.selectionStart = caret;
      element.selectionEnd = caret;
    });
  }
  
  useEffect(() => {
    if(onOuterChange) onOuterChange(innerValue.getNumericValue())
  }, [innerValue, onOuterChange])
  

  return ( renderer ? renderer(innerValue.getPrintingValue(), onChange, onFocus, onBlur, disabled, decorator, label)
    :
    <>
    {label ? label + ": " : ""}
    <input
      id={id}
      style={style}
      disabled={disabled}
      type="text"
      value={innerValue.getPrintingValue()}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
    /> {decorator}</>
  );
};
